import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Protected routes that should exclude or blur sidebar (onboarding flow)
// desktop/mobile: true = exclude sidebar, false = show sidebar
// blur: { desktop: boolean, mobile: boolean } = blur sidebar instead of excluding
const EXCLUDED_ROUTES = {
  // Profile onboarding
  '/profile/intro': { desktop: false, mobile: true, blur: { desktop: true, mobile: false } },
  // Passport request
  '/passport/request': { desktop: true, mobile: true }
};

// Routes with dynamic segments that should exclude sidebar
// Uses regex patterns for more precise matching
const EXCLUDED_ROUTES_PATTERNS = [
  // Passport documents upload with authCode
  {
    pattern: /^\/passport\/documents\/[^/]+$/,
    config: { desktop: true, mobile: true }
  }
];

/**
 * Hook to determine if the sidebar should be excluded or blurred for the current route
 * @param {number} breakpoint - Window width breakpoint for mobile detection (default: 874)
 */
export function useShouldExcludeSidebar(breakpoint = 874) {
  const location = useLocation();

  // Check if we're on mobile (matching Container breakpoint)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  // Check if current route should exclude or blur sidebar
  const { shouldExcludeSidebar, shouldBlurSidebar } = useMemo(() => {
    const pathname = location.pathname;
    let exclude = false;
    let blur = false;

    // Check exact path matches
    Object.keys(EXCLUDED_ROUTES).forEach((excludedPath) => {
      if (pathname.startsWith(excludedPath)) {
        const routeConfig = EXCLUDED_ROUTES[excludedPath];
        const deviceType = isMobile ? 'mobile' : 'desktop';

        // Check if sidebar should be excluded
        if (routeConfig[deviceType] === true) {
          exclude = true;
        }

        // Check if sidebar should be blurred (only if not excluded)
        if (!exclude && routeConfig.blur && routeConfig.blur[deviceType] === true) {
          blur = true;
        }
      }
    });

    // Check pattern matches
    if (!exclude) {
      EXCLUDED_ROUTES_PATTERNS.forEach((routePattern) => {
        if (routePattern.pattern.test(pathname)) {
          const deviceType = isMobile ? 'mobile' : 'desktop';
          const routeConfig = routePattern.config;

          // Check if sidebar should be excluded
          if (routeConfig[deviceType] === true) {
            exclude = true;
          }

          // Check if sidebar should be blurred (only if not excluded)
          if (!exclude && routeConfig.blur && routeConfig.blur[deviceType] === true) {
            blur = true;
          }
        }
      });
    }

    return { shouldExcludeSidebar: exclude, shouldBlurSidebar: blur };
  }, [location.pathname, isMobile]);

  return { shouldExcludeSidebar, shouldBlurSidebar };
}

